<template>
  <BaseTable
    :isLoading="isLoading"
    :tableData="data"
    :totalItemsCount="totalItemsCount"
    :perPage="perPage"
    :currentPage="currentPage"
    :showSearch="true"
    searchPlaceholder="Search by username"
    :showCreateButton="rolPermissions.isAllowedCreate"
    createLabel="New User"
    @create="$emit('openModal')"
    @search="onSearch"
    @clear="onClear"
    @paginate="paginate"
  >
    <template #filters>
      <Dropdown
        v-if="loggedUser && loggedUser.role === 'department_admin'"
        class="data-table__container-filter--dropdown"
        v-model="departmentId"
        :options="userDepartments"
        optionLabel="name"
        optionValue="id"
        @change="getUsers()"
      />
    </template>

    <template #columns>
      <Column field="name" header="Username" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ data.username }}</p>
        </template>
      </Column>
      <Column field="company" header="Company" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          {{ data.company_name }}
          <p v-if="data.company_name === null">All</p>
        </template>
      </Column>
      <Column field="departments" header="Department" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p v-if="data.departments.length === 0">All</p>
          <p v-else v-for="(dep, index) in data.departments" :key="index">{{ dep.name }}</p>
        </template>
      </Column>
      <Column field="status" header="Status" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ data.status }}</p>
        </template>
      </Column>
    </template>

    <template #actions-column>
      <Column header="Actions">
        <template #body="{ data }">
          <div class="data-table__actions">
            <span v-if="rolPermissions.isAllowedEdit" @click="$emit('openModal', data)">
              <i class="pi pi-pencil"></i>
            </span>
            <span v-if="rolPermissions.isAllowedDelete" @click="deleteRow(data)">
              <i class="pi pi-trash"></i>
            </span>
          </div>
        </template>
      </Column>
    </template>
  </BaseTable>
</template>

<script>
import { mapGetters } from "vuex";
import { successMessage, deleteMessage } from "@/utils/alertMessages";
import { userInformation, rolPermissions } from "@/mixins/itemsTable/infoUser";
import BaseTable from "./BaseTable.vue";

export default {
  name: "UserTable",
  components: { BaseTable },
  props: ["firstPage"],
  emits: ["openModal"],
  data() {
    return {
      isLoading: false,
      data: [],
      loggedUser: null,
      rolPermissions: {},
      confirmationDeleteMessage: "I'm sure I want to delete this",
      totalItemsCount: 0,
      currentPage: 1,
      perPage: 15,
      userDepartments: [],
      departmentId: null,
    };
  },
  computed: {
    ...mapGetters("user", ["showCurrentCompanyDepartment"]),
  },
  watch: {
    async showCurrentCompanyDepartment() {
      if (this.showCurrentCompanyDepartment) {
        this.currentPage = 1;
        await this.getUsers();
        this.getUserPermissions();
      }
    },
    firstPage() {
      this.currentPage = 1;
    },
  },
  async created() {
    this.loggedUser = JSON.parse(localStorage.getItem("user-info"));
    if (this.loggedUser.role === "department_admin") {
      this.userDepartments = this.loggedUser.company_info[0].departments;
      this.departmentId = this.loggedUser.company_info[0].departments[0].id;
    }
    await this.getUsers();
    this.getUserPermissions();
  },
  methods: {
    getUserInfo() {
      return userInformation(this.loggedUser);
    },
    getUserPermissions() {
      this.rolPermissions = rolPermissions(this.loggedUser, "User");
    },
    async getUsers(params) {
      this.isLoading = true;
      const user = this.getUserInfo();
      user.limit = 15;
      user.skip = (this.currentPage - 1) * 15;
      if (params?.name) user.username = params.name;
      if (this.loggedUser.role === "department_admin") {
        user.department_id = this.departmentId;
      }
      const info = await this.$store.dispatch("user/getUsersCompleteInfo", user);
      this.totalItemsCount = info.count;
      this.data = info.data;
      this.isLoading = false;
    },
    async onSearch(term) {
      if (term) {
        this.currentPage = 1;
        await this.getUsers({ name: term });
      }
    },
    async onClear() {
      this.currentPage = 1;
      await this.getUsers();
    },
    async paginate(page) {
      this.currentPage = page;
      await this.getUsers();
      this.getUserPermissions();
      window.scrollTo(0, 0);
    },
    async deleteRow(userData) {
      const msg = "Are you sure you want to remove User from the system?";
      const warningResponse = await deleteMessage(msg, "You won't be able to revert this!");
      if (warningResponse.isConfirmed) {
        const params = this.getUserInfo();
        params.user_id = userData.id;
        params.confirmation_message = this.confirmationDeleteMessage;
        if (this.loggedUser.role === "department_admin") {
          params.department_id = userData.departments[0].id;
        }
        const response = await this.$store.dispatch("user/deleteUser", params);
        if (response.status === 200 || response.status === 204) {
          successMessage("The user has been deleted.");
          if (this.data.length === 1 && this.currentPage > 1) this.currentPage -= 1;
          await this.getUsers();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@include table-action-icons;
</style>
